import React, { useEffect, useRef } from 'react';

import { MagicWand } from '@styled-icons/boxicons-solid/MagicWand';
import { InfoOutline } from '@styled-icons/evaicons-outline/InfoOutline';
import { Spinner3 } from '@styled-icons/evil/Spinner3';
import { Close } from '@styled-icons/ionicons-outline/Close';
import _ from 'lodash';
import { Dialog, Button, useDialog } from 'ui-kit';

import { gql, useLazyQuery } from '@apollo/client';

import { LoanField } from '__generated__/globalTypes';

import { AutoMapFields, AutoMapFieldsVariables } from './__generated__/AutoMapFields';
import { FieldIdAndLabel, FieldsMap } from './MapRequiredFieldsCard';

const AUTO_MAP_FIELDS_QUERY = gql`
  query AutoMapFields($input: AutoMapFieldsInput!) {
    autoMapFields(input: $input) {
      matches {
        csvHeader
        loanField
      }
      errors {
        missingEnums
        multipleHeadersToSingleEnum
        detailedMessage
      }
    }
  }
`;

type LLMAutoMapComponentProps = {
  requiredFields: FieldIdAndLabel[];
  optionalFields: FieldIdAndLabel[];
  columns: string[];
  fileId?: string;
  onAutoMapComplete: (fieldsMap: FieldsMap, remainingUnmappedHeaders: string[]) => void;
  onAutoMapLoadingChange?: (isLoading: boolean) => void;
};

const LLMAutoMapComponent: React.FC<LLMAutoMapComponentProps> = ({
  requiredFields,
  optionalFields,
  columns,
  fileId,
  onAutoMapComplete,
  onAutoMapLoadingChange,
}) => {
  const dialog = useDialog();
  const fieldEnums = requiredFields.concat(optionalFields);
  const manuallySetLoadingRef = useRef(false);
  const queryStartTimeRef = useRef<number | null>(null);
  const [autoMapFields, { loading }] = useLazyQuery<AutoMapFields, AutoMapFieldsVariables>(
    AUTO_MAP_FIELDS_QUERY,
    {
      onCompleted: (data) => {
        const fieldsMap = {} as FieldsMap;
        const matchResults =
          (data.autoMapFields.matches?.filter(
            (match) => match != null && !!match.loanField && !!match.csvHeader,
          ) as { loanField: LoanField; csvHeader: string }[]) || [];
        requiredFields.forEach((field) => {
          const match = matchResults.find((m) => m.loanField === field.fieldId);
          if (match && !_.values(fieldsMap).includes(match.csvHeader)) {
            fieldsMap[field.fieldId] = match.csvHeader;
          }
        });
        optionalFields.forEach((field) => {
          const match = matchResults.find((m) => m.loanField === field.fieldId);
          if (match && !_.values(fieldsMap).includes(match.csvHeader)) {
            fieldsMap[field.fieldId] = match.csvHeader;
          }
        });
        if (data.autoMapFields.errors != null) {
          console.error(data.autoMapFields.errors);
        }
        const remainingHeaders = columns.filter((header) => !_.values(fieldsMap).includes(header));
        onAutoMapComplete(fieldsMap, remainingHeaders);
        manuallySetLoadingRef.current = false;
        queryStartTimeRef.current = null;
      },
      onError: () => {
        // Ensure loading state is cleared on error
        onAutoMapLoadingChange?.(false);
        manuallySetLoadingRef.current = false;
        queryStartTimeRef.current = null;
      },
    },
  );

  // Sync with Apollo's loading state
  useEffect(() => {
    if (!manuallySetLoadingRef.current || loading) {
      onAutoMapLoadingChange?.(loading);
      if (loading) {
        manuallySetLoadingRef.current = false;
        queryStartTimeRef.current = null;
      }
    }
  }, [loading, onAutoMapLoadingChange]);

  // Safety check: if we manually set loading but query never started,
  // clear the loading state after a timeout to prevent it from getting stuck
  useEffect(() => {
    if (manuallySetLoadingRef.current && queryStartTimeRef.current !== null) {
      const timeoutId = setTimeout(() => {
        if (manuallySetLoadingRef.current && !loading) {
          onAutoMapLoadingChange?.(false);
          manuallySetLoadingRef.current = false;
          queryStartTimeRef.current = null;
        }
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [loading, onAutoMapLoadingChange]);

  const handleAutoMapClick = () => {
    // if fieldIds or columns are empty, don't call autoMapFields
    if (!fieldEnums.length || !columns.length) {
      console.error('LLMAutoMap: Field enums or columns are empty');
      return;
    }

    // Notify parent immediately to lock UI before query starts
    manuallySetLoadingRef.current = true;
    queryStartTimeRef.current = Date.now();
    onAutoMapLoadingChange?.(true);

    dialog.openDialog();
    const fieldIds = fieldEnums.map((f) => f.fieldId);
    autoMapFields({
      variables: {
        input: {
          columns: columns,
          enums: fieldIds,
          fileId: fileId,
        },
      },
    });
  };

  return (
    <>
      <Dialog dialog={dialog}>
        <span
          className="absolute right-4 top-4 inline-block hover:cursor-pointer"
          onClick={dialog.closeDialog}
          data-testid="ai-automap-info-dialog-close"
        >
          <Close size={24} className="text-foreground-subtle" />
        </span>
        <div className="flex flex-row gap-6" data-testid="ai-automap-info-dialog">
          <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-info-muted">
            <InfoOutline size={24} className="text-black" />
          </div>
          <div className="flex w-[400px] flex-col text-left">
            <h2 className="mb-2 text-xl">AI-Powered Mapping</h2>
            <p className="text-base">Field mapping is conducted by an AI model.</p>
            <p className="text-base">
              The model runs fully within your AWS environment—no data leaves your instance. Please
              review and confirm the results to improve accuracy over time.
            </p>
            <div className="mt-2 flex justify-end">
              <Button
                onClick={dialog.closeDialog}
                label="Close dialog"
                data-testid="ai-automap-info-dialog-ok"
              >
                OK
              </Button>
            </div>
          </div>
        </div>
      </Dialog>

      <div className="mr-5">
        <Button
          type="secondary"
          onClick={handleAutoMapClick}
          disabled={loading}
          label="AI Automap"
          data-testid="ai-automap-button"
        >
          {loading ? (
            <Spinner3 size={20} className="mr-1 animate-spin" />
          ) : (
            <MagicWand size={20} className="mr-1" />
          )}
          AI Automap
        </Button>
      </div>
    </>
  );
};

export default LLMAutoMapComponent;
